import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiZap, FiTrendingUp, FiClock, FiAward } = FiIcons;

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,128,255,0.1),transparent_70%)]"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center min-h-[80vh] lg:min-h-screen">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center space-x-2 mb-4 md:mb-6"
            >
              <SafeIcon icon={FiAward} className="h-5 w-5 text-secondary-500" />
              <span className="text-secondary-500 font-semibold tracking-wide uppercase text-xs sm:text-sm">
                #1 Business Automation in Australia
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6"
            >
              Save 70% Costs with <span className="block text-primary-400">Smart Automation</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed"
            >
              🚀 Transform your business with intelligent automation. Reduce costs by 70%, boost efficiency 3x, and scale without limits. Trusted by 500+ Australian businesses.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 mb-8 md:mb-12"
            >
              <a
                href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-primary-600 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg hover:bg-primary-700 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get FREE Automation Audit</span>
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300 font-semibold text-base sm:text-lg"
              >
                View Automation Solutions
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-3 gap-4 md:gap-8"
            >
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold text-primary-400 mb-1 md:mb-2">70%</div>
                <div className="text-xs md:text-sm text-gray-400">Cost Reduction</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold text-primary-400 mb-1 md:mb-2">3x</div>
                <div className="text-xs md:text-sm text-gray-400">Faster Processes</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold text-primary-400 mb-1 md:mb-2">500+</div>
                <div className="text-xs md:text-sm text-gray-400">Happy Clients</div>
              </div>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-6 md:mt-8 flex flex-wrap items-center gap-4 md:gap-6 text-xs sm:text-sm text-gray-400"
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="h-3 w-3 md:h-4 md:w-4 text-primary-400" />
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAward} className="h-3 w-3 md:h-4 md:w-4 text-primary-400" />
                <span>99% Satisfaction Rate</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="h-3 w-3 md:h-4 md:w-4 text-primary-400" />
                <span>Quick Implementation</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Business automation dashboard showing cost savings and efficiency improvements"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover object-center"
              />

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -top-4 -right-4 md:-top-6 md:-right-6 bg-secondary-500 text-white p-2 md:p-4 rounded-xl shadow-lg"
              >
                <div className="text-center">
                  <div className="text-base md:text-lg font-bold">70%</div>
                  <div className="text-xs">Cost Saved</div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-primary-600 text-white p-2 md:p-4 rounded-xl shadow-lg"
              >
                <div className="text-center">
                  <div className="text-base md:text-lg font-bold">3x</div>
                  <div className="text-xs">Efficiency</div>
                </div>
              </motion.div>
            </div>

            {/* Background Decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-secondary-500/20 rounded-2xl -rotate-6 scale-105 -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;